import { BadgeCheck, ChevronRight, Network, Plus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChannelBrandProfileInput, ChannelRecord, CreateChannelRequest } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useChannelsList, useCreateChannel, useDeleteChannel } from '../api/useChannels.ts';
import { useChannelModelsList } from '../api/useChannelModels.ts';
import { useVendorsList } from '../api/useVendors.ts';
import { formatLocalDate } from '../components/formatters.ts';
import { ChannelBrandBindingDrawer } from '../components/ChannelBrandBindingDrawer.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import {
  CHANNEL_BRANDS,
  brandLabel,
  enabledBrandProfiles,
  type ChannelBrandId,
} from '../lib/channelBrands.ts';
import {
  Button,
  Checkbox,
  Chip,
  ConfirmDialog,
  Dialog,
  EmptyState,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  Table,
  Tabs,
  TabsList,
  TabsTrigger,
  TBody,
  Td,
  Textarea,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * Namespace registry (epic #898 P6, #910) — the thin identity half of
 * the Model Market IA. Registry CRUD only: slug, display name, vendor,
 * description, supplier, official flag, brand profiles. Pricing lives
 * at the SKU grain — deliberately NO rate fields on this surface.
 */

type BrandFilter = '' | ChannelBrandId;

function BrandProfileChips({
  row,
  onManageBrands,
}: {
  readonly row: ChannelRecord;
  readonly onManageBrands: (row: ChannelRecord) => void;
}): JSX.Element {
  const t = useT();
  const profiles = enabledBrandProfiles(row);
  return (
    <div className="flex items-center gap-2">
      <div className="flex min-w-0 flex-col gap-1.5">
        {profiles.map((profile) => (
          <Button
            key={profile.brand}
            variant="secondary"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              onManageBrands(row);
            }}
            className="max-w-[180px] justify-between"
          >
            <span>{brandLabel(profile.brand)}</span>
            {profile.public_slug ? (
              <span className="truncate font-mono font-normal text-paper-low">{profile.public_slug}</span>
            ) : null}
          </Button>
        ))}
        {profiles.length === 0 ? <span className="text-paper-faint">—</span> : null}
      </div>
      <IconButton
        label={t('channels.brand_bindings.manage')}
        onClick={(event) => {
          event.stopPropagation();
          onManageBrands(row);
        }}
        data-testid={`namespace-row-${row.id}-manage-brands`}
      >
        <Network aria-hidden="true" className="h-4 w-4" />
      </IconButton>
    </div>
  );
}

function NamespaceRow({
  row,
  onDelete,
  onManageBrands,
  deleting,
  onNavigate,
  skuCount,
}: {
  readonly row: ChannelRecord;
  readonly onDelete: (row: ChannelRecord) => void;
  readonly onManageBrands: (row: ChannelRecord) => void;
  readonly deleting: boolean;
  readonly onNavigate: (id: string) => void;
  readonly skuCount: number;
}): JSX.Element {
  const t = useT();
  return (
    <Tr
      data-testid={`namespace-row-${row.id}`}
      className="group cursor-pointer"
      onClick={() => onNavigate(row.id)}
    >
      <Td>
        <div className="flex flex-col gap-1">
          <span className="text-body font-medium text-paper">{row.display_name}</span>
          <span
            data-testid={`namespace-row-${row.id}-slug`}
            className="font-mono text-label text-paper-low"
          >
            {row.slug}
          </span>
          {row.description != null ? (
            <span
              data-testid={`namespace-row-${row.id}-description`}
              className="max-w-[280px] text-dense text-paper-low"
            >
              {row.description}
            </span>
          ) : null}
        </div>
      </Td>
      <Td>
        <Chip tone="neutral" lamp={false} className="font-mono">
          {row.vendor}
        </Chip>
      </Td>
      <Td>
        <BrandProfileChips row={row} onManageBrands={onManageBrands} />
      </Td>
      <Td num data-testid={`namespace-row-${row.id}-sku-count`}>
        {skuCount > 0 ? skuCount : <span className="text-paper-faint">—</span>}
      </Td>
      <Td>{row.supplier_name ?? <span className="text-paper-faint">—</span>}</Td>
      <Td className="text-center">
        {row.is_official ? (
          <BadgeCheck aria-hidden="true" className="inline-block h-4 w-4 text-signal" />
        ) : (
          <span className="text-paper-faint">—</span>
        )}
      </Td>
      <Td>{formatLocalDate(row.created_at)}</Td>
      <Td className="text-right">
        <div className="flex items-center justify-end gap-1">
          <IconButton
            size="sm"
            label={t('namespaces.row.delete')}
            onClick={(event) => {
              event.stopPropagation();
              onDelete(row);
            }}
            disabled={deleting}
            data-testid={`namespace-row-${row.id}-delete`}
            className="text-bad hover:bg-bad-dim hover:text-bad"
          >
            <Trash2 aria-hidden="true" className="h-4 w-4" />
          </IconButton>
          <ChevronRight
            aria-hidden="true"
            className="h-4 w-4 text-paper-low transition-colors duration-120 group-hover:text-signal"
          />
        </div>
      </Td>
    </Tr>
  );
}

export function NamespacesPage(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const [brandFilter, setBrandFilter] = useState<BrandFilter>('');
  const { data: channels, isLoading, error } = useChannelsList(brandFilter === '' ? undefined : brandFilter);
  const { data: allChannelModels } = useChannelModelsList();
  const vendorsQ = useVendorsList();
  const createMut = useCreateChannel();
  const deleteMut = useDeleteChannel();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [brandDrawerChannel, setBrandDrawerChannel] = useState<ChannelRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ChannelRecord | null>(null);
  const [createVendor, setCreateVendor] = useState('');
  const [createOfficial, setCreateOfficial] = useState(false);
  const [launchBrands, setLaunchBrands] = useState<ReadonlySet<ChannelBrandId>>(
    () => new Set(CHANNEL_BRANDS.map(({ id }) => id)),
  );

  const skuStats = useMemo(() => {
    const stats = new Map<string, number>();
    for (const cm of allChannelModels ?? []) {
      stats.set(cm.channel_id, (stats.get(cm.channel_id) ?? 0) + 1);
    }
    return stats;
  }, [allChannelModels]);

  const filtered = useMemo(() => {
    if (!channels) return [];
    if (search.length === 0) return channels;
    const q = search.toLowerCase();
    return channels.filter(
      (c) =>
        c.slug.toLowerCase().includes(q) ||
        c.display_name.toLowerCase().includes(q) ||
        (c.description ?? '').toLowerCase().includes(q) ||
        (c.supplier_name ?? '').toLowerCase().includes(q),
    );
  }, [channels, search]);

  function openCreate(): void {
    setCreateVendor('');
    setCreateOfficial(false);
    setLaunchBrands(new Set(CHANNEL_BRANDS.map(({ id }) => id)));
    setCreateOpen(true);
  }

  function toggleLaunchBrand(id: ChannelBrandId, checked: boolean): void {
    setLaunchBrands((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  function confirmDelete(): void {
    if (deleteTarget === null) {
      return;
    }
    deleteMut.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(t('namespaces.toast.deleted'));
        setDeleteTarget(null);
      },
      onError: (err) => {
        toast.error(coerceErrorMessage(err));
        setDeleteTarget(null);
      },
    });
  }

  function handleCreate(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const brandProfiles: ChannelBrandProfileInput[] = CHANNEL_BRANDS.flatMap(({ id }) => {
      if (!launchBrands.has(id)) return [];
      return {
        brand: id,
        public_slug: null,
        display_name: null,
        enabled: true,
      };
    });
    // Registry-only create: no rate fields on this surface. The platform
    // seeds the namespace multiplier at 1.0 and every new SKU inherits it
    // until an explicit value is set on the SKU detail page.
    const body: CreateChannelRequest = {
      vendor: createVendor.trim(),
      slug: (fd.get('slug') as string).trim(),
      display_name: (fd.get('display_name') as string).trim(),
      description: ((fd.get('description') as string) || '').trim() || null,
      supplier_name: ((fd.get('supplier_name') as string) || '').trim() || undefined,
      is_official: createOfficial,
      brand_profiles: brandProfiles,
    };
    createMut.mutate(body, {
      onSuccess: () => {
        toast.success(t('namespaces.toast.created'));
        setCreateOpen(false);
      },
      onError: (err) => {
        if (err instanceof OpsApiError && err.code === 'CHANNEL_ALREADY_EXISTS') {
          toast.error(t('namespaces.toast.duplicate'));
        } else if (err instanceof OpsApiError && err.code === 'CHANNEL_ALIAS_ALREADY_EXISTS') {
          toast.error(t('namespaces.toast.alias_duplicate'));
        } else {
          toast.error(coerceErrorMessage(err));
        }
      },
    });
  }

  if (isLoading) {
    return (
      <div data-testid="namespaces-loading" className="flex flex-col gap-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="namespaces-error"
        className="rounded-lg border border-bad-line bg-bad-dim px-4 py-3 text-body text-bad-bright"
      >
        {coerceErrorMessage(error)}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('namespaces.title')}
        sub={t('namespaces.subtitle')}
        actions={
          <Button variant="primary" onClick={openCreate} data-testid="namespaces-create-btn">
            <Plus aria-hidden="true" className="h-4 w-4" />
            {t('namespaces.create_btn')}
          </Button>
        }
      />

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('namespaces.search.placeholder')}
            data-testid="namespaces-search"
            className="pr-9"
          />
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-paper-low"
          />
        </div>
        <Tabs
          value={brandFilter === '' ? 'all' : brandFilter}
          onValueChange={(next) => {
            setBrandFilter(CHANNEL_BRANDS.find(({ id }) => id === next)?.id ?? '');
          }}
          className="shrink-0"
        >
          <TabsList ariaLabel={t('namespaces.brand_filter_aria')}>
            <TabsTrigger value="all" testId="namespaces-brand-filter-all">
              {t('namespaces.brand_filter.all')}
            </TabsTrigger>
            {CHANNEL_BRANDS.map(({ id, label }) => (
              <TabsTrigger key={id} value={id} testId={`namespaces-brand-filter-${id}`}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {filtered.length === 0 ? (
        <EmptyState testId="namespaces-empty" title={t('namespaces.empty')} />
      ) : (
        <Table testId="namespaces-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('namespaces.col.display_name')}</Th>
              <Th>{t('namespaces.col.vendor')}</Th>
              <Th>{t('namespaces.col.brand_profiles')}</Th>
              <Th num>{t('namespaces.col.skus')}</Th>
              <Th>{t('namespaces.col.supplier')}</Th>
              <Th className="text-center">{t('namespaces.col.official')}</Th>
              <Th>{t('namespaces.col.created_at')}</Th>
              <Th />
            </Tr>
          </THead>
          <TBody>
            {filtered.map((ch) => (
              <NamespaceRow
                key={ch.id}
                row={ch}
                onDelete={setDeleteTarget}
                onManageBrands={setBrandDrawerChannel}
                deleting={deleteMut.isPending}
                onNavigate={(cid) => navigate(`/supply/namespaces/${cid}`)}
                skuCount={skuStats.get(ch.id) ?? 0}
              />
            ))}
          </TBody>
        </Table>
      )}

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          if (!open) setCreateOpen(false);
        }}
        title={t('namespaces.create_modal.title')}
        closeLabel={t('common.close')}
        size="lg"
      >
        <form onSubmit={handleCreate} noValidate className="flex flex-col gap-4">
          <FormField label={t('namespaces.create_modal.vendor')} fieldId="namespace-create-vendor">
            <Select
              value={createVendor === '' ? undefined : createVendor}
              onValueChange={setCreateVendor}
              placeholder={t('namespaces.create_modal.select_vendor')}
              options={(vendorsQ.data ?? []).map((v) => ({ value: v.slug, label: v.display_name }))}
              ariaLabel={t('namespaces.create_modal.vendor')}
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label={`Slug (${t('channels.create_modal.slug_hint')})`}
              fieldId="namespace-create-slug"
            >
              <Input id="namespace-create-slug" name="slug" mono placeholder="e.g. custom-endpoint" />
            </FormField>
            <FormField label={t('channels.create_modal.supplier_name')} fieldId="namespace-create-supplier">
              <Input
                id="namespace-create-supplier"
                name="supplier_name"
                placeholder={t('channels.create_modal.supplier_placeholder')}
              />
            </FormField>
          </div>

          <FormField label={t('channels.create_modal.display_name')} fieldId="namespace-create-display-name">
            <Input
              id="namespace-create-display-name"
              name="display_name"
              placeholder={t('channels.create_modal.display_name_placeholder')}
            />
          </FormField>

          <FormField
            label={t('channels.create_modal.description')}
            fieldId="namespace-create-description"
            hint={t('channels.create_modal.description_hint')}
          >
            <Textarea
              id="namespace-create-description"
              name="description"
              rows={3}
              className="resize-none"
              placeholder={t('channels.create_modal.description_placeholder')}
            />
          </FormField>

          <p
            className="m-0 rounded-lg border border-line-soft bg-well px-4 py-3 text-dense leading-5 text-paper-low"
            data-testid="namespace-create-pricing-note"
          >
            {t('namespaces.create_modal.pricing_note')}
          </p>

          <div className="flex items-center justify-between rounded-lg border border-line-soft bg-well p-4">
            <div>
              <p className="m-0 text-body font-medium text-paper">{t('channels.create_modal.is_official')}</p>
              <p className="m-0 mt-0.5 text-dense text-paper-low">{t('channels.create_modal.is_official_hint')}</p>
            </div>
            <Switch
              checked={createOfficial}
              onCheckedChange={setCreateOfficial}
            />
          </div>

          <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
            <legend className="p-0 text-label text-paper-mid">
              {t('channels.create_modal.launch_brands')}
            </legend>
            <div className="flex flex-wrap gap-2">
              {CHANNEL_BRANDS.map(({ id, label }) => (
                <span
                  key={id}
                  className="inline-flex items-center rounded-lg border border-line-soft bg-well px-3 py-2"
                >
                  <Checkbox
                    checked={launchBrands.has(id)}
                    onCheckedChange={(checked) => toggleLaunchBrand(id, checked)}
                    label={label}
                    testId={`namespace-create-brand-${id}`}
                  />
                </span>
              ))}
            </div>
            <p className="m-0 text-label text-paper-low">{t('channels.create_modal.launch_brands_hint')}</p>
          </fieldset>

          {createMut.isError && (
            <div
              className="rounded border border-bad-line bg-bad-dim px-3 py-2 text-dense text-bad-bright"
              data-testid="namespaces-create-error"
              role="alert"
            >
              {createMut.error instanceof OpsApiError
                ? createMut.error.message
                : coerceErrorMessage(createMut.error)}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              {t('channels.create_modal.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={createMut.isPending}>
              {createMut.isPending ? t('channels.create_modal.creating') : t('channels.create_modal.submit')}
            </Button>
          </div>
        </form>
      </Dialog>
      <ChannelBrandBindingDrawer
        channel={brandDrawerChannel}
        isOpen={brandDrawerChannel !== null}
        onClose={() => setBrandDrawerChannel(null)}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleteMut.isPending) {
            setDeleteTarget(null);
          }
        }}
        title={t('namespaces.delete_modal.title')}
        body={t('namespaces.delete_modal.body', { name: deleteTarget?.display_name ?? '' })}
        confirmLabel={deleteMut.isPending ? t('common.deleting') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        onConfirm={confirmDelete}
        tone="danger"
        loading={deleteMut.isPending}
        testId="namespaces-delete-confirm"
      />
    </div>
  );
}
